import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import adminService from '../services/adminService';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';

const AdminReportsPage = () => {
  const { isMasterAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedUsers, setExpandedUsers] = useState(new Set());

  useEffect(() => {
    // Redirect if not master admin
    if (!isMasterAdmin) {
      navigate('/dashboard');
      return;
    }

    fetchAllData();
  }, [isMasterAdmin, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryData, usersData, activityData] = await Promise.all([
        adminService.getSummary(),
        adminService.getUserReports(),
        adminService.getActivity(100)
      ]);

      setSummary(summaryData);
      setUsers(usersData);
      setActivity(activityData);
    } catch (error) {
      console.error('Error fetching admin reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserExpanded = (uid) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(uid)) {
      newExpanded.delete(uid);
    } else {
      newExpanded.add(uid);
    }
    setExpandedUsers(newExpanded);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (!isMasterAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-lg text-gray-600">Loading admin reports...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Reports</h1>
        <p className="text-gray-600">System usage and user activity analytics</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('summary')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'summary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'activity'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Activity Feed
          </button>
        </nav>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Users</h3>
              <p className="text-4xl font-bold text-blue-600">{summary.totalUsers}</p>
              <p className="text-sm text-gray-600 mt-2">
                {summary.activeUsers} active (30 days)
              </p>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Teams</h3>
              <p className="text-4xl font-bold text-green-700">{summary.totalTeams}</p>
              <div className="mt-2 text-sm text-gray-600">
                <div>AVPs: {summary.teamsByRole.avp}</div>
                <div>Directors: {summary.teamsByRole.director}</div>
                <div>Managers: {summary.teamsByRole.manager}</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Total Scenarios</h3>
              <p className="text-4xl font-bold text-purple-600">{summary.totalScenarios}</p>
              <p className="text-sm text-gray-600 mt-2">
                {(summary.totalScenarios / (summary.totalUsers || 1)).toFixed(1)} per user avg
              </p>
            </div>
          </Card>

          <Card className="md:col-span-2 lg:col-span-3">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">User Engagement Rate</span>
                  <span className="font-semibold">
                    {((summary.activeUsers / summary.totalUsers) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${(summary.activeUsers / summary.totalUsers) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {users.map((user) => (
            <Card key={user.uid}>
              <div className="p-6">
                <div
                  className="flex justify-between items-start cursor-pointer"
                  onClick={() => toggleUserExpanded(user.uid)}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{user.email}</h3>
                      {user.displayName && (
                        <span className="text-sm text-gray-600">({user.displayName})</span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Teams:</span>
                        <span className="ml-2 font-medium">{user.stats.teamsCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Scenarios:</span>
                        <span className="ml-2 font-medium">{user.stats.scenariosCount}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Last Activity:</span>
                        <span className="ml-2 font-medium">
                          {formatRelativeTime(user.stats.lastActivity)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    {expandedUsers.has(user.uid) ? '▼' : '▶'}
                  </button>
                </div>

                {expandedUsers.has(user.uid) && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Teams */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Teams</h4>
                        {user.teams.length > 0 ? (
                          <div className="space-y-2">
                            {user.teams.map((team) => (
                              <div key={team.id} className="bg-gray-50 p-3 rounded">
                                <div className="font-medium text-sm">{team.name}</div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {team.role.toUpperCase()} • Updated {formatRelativeTime(team.updatedAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">No teams created</p>
                        )}
                      </div>

                      {/* Scenarios */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Scenarios</h4>
                        {user.scenarios.length > 0 ? (
                          <div className="space-y-2">
                            {user.scenarios.map((scenario) => (
                              <div key={scenario.id} className="bg-gray-50 p-3 rounded">
                                <div className="font-medium text-sm">{scenario.name}</div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Updated {formatRelativeTime(scenario.updatedAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-600">No scenarios created</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Activity Feed Tab */}
      {activeTab === 'activity' && (
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
              <Button size="sm" onClick={fetchAllData}>
                Refresh
              </Button>
            </div>
            <div className="space-y-3">
              {activity.map((item, index) => (
                <div
                  key={`${item.type}-${item.id}-${index}`}
                  className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      item.type === 'team' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                    }`}>
                      {item.type === 'team' ? '👥' : '📊'}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {item.userEmail}
                      <span className="text-gray-600 font-normal"> {item.action} </span>
                      {item.type}
                    </p>
                    <p className="text-sm text-gray-600 truncate">
                      {item.name}
                      {item.role && <span className="ml-2 text-xs text-gray-600">({item.role})</span>}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {formatDate(item.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminReportsPage;
